import { useState, useEffect } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import DeleteIcon from '@mui/icons-material/Delete'
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import * as joinRequestService from '../../services/joinRequestService'

interface AllowlistEntry {
  userId: string
  displayName: string
}

const AllowlistManager = () => {
  const [entries, setEntries] = useState<AllowlistEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchAllowlist()
  }, [])

  const fetchAllowlist = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const userIds = await joinRequestService.getAllowlist()
      const resolved = await resolveDisplayNames(userIds)
      setEntries(resolved)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load allowlist'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const resolveDisplayNames = async (
    userIds: string[]
  ): Promise<AllowlistEntry[]> => {
    const db = getFirestore()
    const results: AllowlistEntry[] = []

    for (const userId of userIds) {
      try {
        const userRef = doc(db, 'users', userId)
        const userSnap = await getDoc(userRef)

        if (userSnap.exists()) {
          const data = userSnap.data()
          results.push({
            userId,
            displayName: data.displayName || 'Unknown User'
          })
        } else {
          results.push({ userId, displayName: 'Unknown User' })
        }
      } catch {
        results.push({ userId, displayName: 'Unknown User' })
      }
    }

    return results
  }

  const handleRemove = async (userId: string) => {
    // Optimistically remove from list
    setEntries(prev => prev.filter(entry => entry.userId !== userId))

    try {
      await joinRequestService.removeFromAllowlist(userId)
    } catch (err) {
      // Revert on failure
      const message =
        err instanceof Error ? err.message : 'Failed to remove from allowlist'
      setError(message)
      // Re-fetch to restore correct state
      fetchAllowlist()
    }
  }

  if (isLoading) {
    return (
      <Card variant='outlined'>
        <CardContent>
          <Stack alignItems='center' spacing={2} sx={{ py: 2 }}>
            <CircularProgress size={24} />
            <Typography variant='body2' color='text.secondary'>
              Loading allowlist...
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card variant='outlined'>
      <CardContent>
        <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
          Auto-approve list
        </Typography>

        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {entries.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            Your allowlist is empty. Use "Always Approve" on a join request to
            add divers here.
          </Typography>
        ) : (
          <List disablePadding>
            {entries.map(entry => (
              <ListItem
                key={entry.userId}
                secondaryAction={
                  <IconButton
                    edge='end'
                    aria-label={`Remove ${entry.displayName}`}
                    onClick={() => handleRemove(entry.userId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText primary={entry.displayName} />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  )
}

export default AllowlistManager
